import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";
import DashboardLayout from "../components/DashboardLayout";
import { availabilityAPI } from "../services/api";

export default function AvailabilityPage() {
    const navigate = useNavigate();
    const { isAuthenticated } = useSelector((state) => state.Auth);

    const [profileData, setProfileData] = useState({});
    const [formData, setFormData] = useState({
        availableDays: [],
        timeSlots: [],
        timezone: "Asia/Kolkata",
    });

    const [loading, setLoading] = useState(false);
    const [fetchingData, setFetchingData] = useState(true);
    const [showTimeSlotModal, setShowTimeSlotModal] = useState(false);
    const [newTimeSlot, setNewTimeSlot] = useState({
        day: "",
        startTime: "",
        endTime: "",
    });

    const daysOfWeek = [
        { id: "monday", name: "Monday", short: "Mon" },
        { id: "tuesday", name: "Tuesday", short: "Tue" },
        { id: "wednesday", name: "Wednesday", short: "Wed" },
        { id: "thursday", name: "Thursday", short: "Thu" },
        { id: "friday", name: "Friday", short: "Fri" },
        { id: "saturday", name: "Saturday", short: "Sat" },
        { id: "sunday", name: "Sunday", short: "Sun" },
    ];

    const timezones = [
        { value: "Asia/Kolkata", label: "IST (India Standard Time) - UTC+5:30" },
        { value: "America/New_York", label: "EST (Eastern Time) - UTC-5" },
        { value: "America/Los_Angeles", label: "PST (Pacific Time) - UTC-8" },
        { value: "Europe/London", label: "GMT (London) - UTC+0" },
        { value: "Europe/Paris", label: "CET (Central Europe) - UTC+1" },
        { value: "Asia/Dubai", label: "GST (Dubai) - UTC+4" },
        { value: "Asia/Singapore", label: "SGT (Singapore) - UTC+8" },
        { value: "Australia/Sydney", label: "AEDT (Sydney) - UTC+11" },
    ];

    useEffect(() => {
        const storedUser = localStorage.getItem("user");
        if (!storedUser && !isAuthenticated) {
            navigate("/login");
            return;
        }

        // Fetch availability data from API
        const fetchAvailabilityData = async () => {
            try {
                setFetchingData(true);
                const response = await availabilityAPI.getAvailability();

                if (response.data.success) {
                    const data = response.data.data;
                    setFormData({
                        availableDays: data.availableDays || [],
                        timeSlots: data.timeSlots || [],
                        timezone: data.timezone || "Asia/Kolkata",
                    });
                }
            } catch (error) {
                console.error("Error fetching availability data:", error);
                if (error.response?.status !== 404) {
                    toast.error("Failed to load availability");
                }
            } finally {
                setFetchingData(false);
            }
        };

        fetchAvailabilityData();
    }, [navigate, isAuthenticated]);

    const handleDayToggle = (dayId) => {
        setFormData((prev) => {
            const availableDays = prev.availableDays.includes(dayId)
                ? prev.availableDays.filter((d) => d !== dayId)
                : [...prev.availableDays, dayId];
            return { ...prev, availableDays };
        });
    };

    const handleTimezoneChange = (e) => {
        setFormData((prev) => ({
            ...prev,
            timezone: e.target.value,
        }));
    };

    const handleTimeSlotInputChange = (e) => {
        const { name, value } = e.target;
        setNewTimeSlot((prev) => ({
            ...prev,
            [name]: value,
        }));
    };

    const handleAddTimeSlot = () => {
        if (!newTimeSlot.day) {
            toast.error("Please select a day");
            return;
        }

        if (!newTimeSlot.startTime || !newTimeSlot.endTime) {
            toast.error("Please select start and end time");
            return;
        }

        if (newTimeSlot.startTime >= newTimeSlot.endTime) {
            toast.error("End time must be after start time");
            return;
        }

        const timeSlot = {
            id: Date.now(),
            ...newTimeSlot,
            dayName: daysOfWeek.find((d) => d.id === newTimeSlot.day)?.name,
        };

        setFormData((prev) => ({
            ...prev,
            timeSlots: [...prev.timeSlots, timeSlot],
        }));

        if (!formData.availableDays.includes(newTimeSlot.day)) {
            setFormData((prev) => ({
                ...prev,
                availableDays: [...prev.availableDays, newTimeSlot.day],
            }));
        }

        setNewTimeSlot({
            day: "",
            startTime: "",
            endTime: "",
        });
        setShowTimeSlotModal(false);
        toast.success("Time slot added successfully!");
    };

    const handleDeleteTimeSlot = (slotId) => {
        if (window.confirm("Are you sure you want to delete this time slot?")) {
            setFormData((prev) => ({
                ...prev,
                timeSlots: prev.timeSlots.filter((slot) => slot.id !== slotId),
            }));
            toast.success("Time slot deleted successfully!");
        }
    };

    const handleQuickSetup = (preset) => {
        let days = [];
        let slots = [];

        switch (preset) {
            case "weekdays":
                days = ["monday", "tuesday", "wednesday", "thursday", "friday"];
                slots = days.map((day, index) => ({
                    id: Date.now() + index,
                    day,
                    dayName: daysOfWeek.find((d) => d.id === day)?.name,
                    startTime: "09:00",
                    endTime: "18:00",
                }));
                break;
            case "weekends":
                days = ["saturday", "sunday"];
                slots = days.map((day, index) => ({
                    id: Date.now() + index,
                    day,
                    dayName: daysOfWeek.find((d) => d.id === day)?.name,
                    startTime: "08:00",
                    endTime: "14:00",
                }));
                break;
            case "everyday":
                days = daysOfWeek.map((d) => d.id);
                slots = days.map((day, index) => ({
                    id: Date.now() + index,
                    day,
                    dayName: daysOfWeek.find((d) => d.id === day)?.name,
                    startTime: "06:00",
                    endTime: "20:00",
                }));
                break;
            default:
                break;
        }

        setFormData((prev) => ({
            ...prev,
            availableDays: days,
            timeSlots: slots,
        }));
        toast.success("Quick setup applied!");
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (formData.availableDays.length === 0) {
            toast.error("Please select at least one available day");
            return;
        }

        if (formData.timeSlots.length === 0) {
            toast.error("Please add at least one time slot");
            return;
        }

        setLoading(true);

        try {
            // Call API to update availability
            const response = await availabilityAPI.updateAvailability(formData);

            if (response.data.success) {
                toast.success("Availability updated successfully!");

                // Update form data with response
                setFormData({
                    availableDays: response.data.data.availableDays || [],
                    timeSlots: response.data.data.timeSlots || [],
                    timezone: response.data.data.timezone || "Asia/Kolkata",
                });
            } else {
                toast.error(response.data.message || "Failed to update availability");
            }
        } catch (error) {
            console.error("Error updating availability:", error);
            toast.error(
                error.response?.data?.message ||
                "Failed to update availability. Please try again."
            );
        } finally {
            setLoading(false);
        }
    };

    const getCompletionPercentage = () => {
        let completed = 0;
        if (formData.availableDays.length > 0) completed += 50;
        if (formData.timeSlots.length > 0) completed += 50;
        return completed;
    };

    const completionPercentage = getCompletionPercentage();

    const getTimeSlotsByDay = () => {
        const slotsByDay = {};
        daysOfWeek.forEach((day) => {
            slotsByDay[day.id] = formData.timeSlots.filter((slot) => slot.day === day.id);
        });
        return slotsByDay;
    };

    const slotsByDay = getTimeSlotsByDay();

    return (
        <DashboardLayout activeTab="availability">
            <div className="availability-tab">
                <div className="tab-header">
                    <div>
                        <h3>
                            <i className="bi bi-calendar-check-fill me-2"></i>
                            Availability & Schedule
                        </h3>
                        <p className="text-muted">Set your working hours and availability</p>
                    </div>
                    <div className="completion-badge">
                        <div className="mini-progress-circle">
                            <svg width="60" height="60">
                                <circle cx="30" cy="30" r="25" fill="none" stroke="#e9ecef" strokeWidth="5" />
                                <circle
                                    cx="30"
                                    cy="30"
                                    r="25"
                                    fill="none"
                                    stroke="#cc0000"
                                    strokeWidth="5"
                                    strokeDasharray={`${(completionPercentage / 100) * 157} 157`}
                                    transform="rotate(-90 30 30)"
                                    strokeLinecap="round"
                                />
                            </svg>
                            <div className="mini-percentage">{completionPercentage}%</div>
                        </div>
                    </div>
                </div>

                {fetchingData ? (
                    <div className="text-center py-5">
                        <div className="spinner-border text-danger" role="status">
                            <span className="visually-hidden">Loading...</span>
                        </div>
                        <p className="text-muted mt-3">Loading availability...</p>
                    </div>
                ) : (
                    <form onSubmit={handleSubmit} className="profile-form">
                        {/* Quick Setup */}
                        <div className="form-section">
                            <h5 className="section-title">
                                <i className="bi bi-lightning-charge-fill me-2"></i>
                                Quick Setup
                            </h5>
                            <p className="text-muted mb-3">Apply a preset schedule to get started quickly</p>

                            <div className="quick-setup-buttons">
                                <button
                                    type="button"
                                    className="btn btn-outline-primary"
                                    onClick={() => handleQuickSetup("weekdays")}
                                >
                                    <i className="bi bi-briefcase me-2"></i>
                                    Weekdays (Mon-Fri, 9 AM - 6 PM)
                                </button>
                                <button
                                    type="button"
                                    className="btn btn-outline-primary"
                                    onClick={() => handleQuickSetup("weekends")}
                                >
                                    <i className="bi bi-calendar2-week me-2"></i>
                                    Weekends (Sat-Sun, 8 AM - 2 PM)
                                </button>
                                <button
                                    type="button"
                                    className="btn btn-outline-primary"
                                    onClick={() => handleQuickSetup("everyday")}
                                >
                                    <i className="bi bi-calendar-check me-2"></i>
                                    Every Day (6 AM - 8 PM)
                                </button>
                            </div>
                        </div>

                        {/* Available Days */}
                        <div className="form-section">
                            <h5 className="section-title">
                                <i className="bi bi-calendar3 me-2"></i>
                                Available Days <span className="text-danger">*</span>
                            </h5>
                            <p className="text-muted mb-3">Select the days you're available for training</p>

                            <div className="days-grid">
                                {daysOfWeek.map((day) => (
                                    <div
                                        key={day.id}
                                        className={`day-card ${formData.availableDays.includes(day.id) ? "selected" : ""}`}
                                        onClick={() => handleDayToggle(day.id)}
                                    >
                                        <div className="day-short">{day.short}</div>
                                        <div className="day-name">{day.name}</div>
                                        {formData.availableDays.includes(day.id) && (
                                            <div className="day-check">
                                                <i className="bi bi-check-circle-fill"></i>
                                            </div>
                                        )}
                                    </div>
                                ))}
                            </div>

                            {formData.availableDays.length > 0 && (
                                <div className="selected-count mt-3">
                                    <i className="bi bi-check-circle-fill text-success me-2"></i>
                                    <strong>{formData.availableDays.length}</strong> day
                                    {formData.availableDays.length > 1 ? "s" : ""} selected
                                </div>
                            )}
                        </div>

                        {/* Time Slots */}
                        <div className="form-section">
                            <div className="section-header-with-action">
                                <div>
                                    <h5 className="section-title">
                                        <i className="bi bi-clock-fill me-2"></i>
                                        Time Slots <span className="text-danger">*</span>
                                    </h5>
                                    <p className="text-muted">Add specific time slots for each day</p>
                                </div>
                                <button
                                    type="button"
                                    className="btn btn-danger"
                                    onClick={() => setShowTimeSlotModal(true)}
                                >
                                    <i className="bi bi-plus-circle me-2"></i>
                                    Add Time Slot
                                </button>
                            </div>

                            {formData.timeSlots.length > 0 ? (
                                <div className="time-slots-calendar">
                                    {daysOfWeek.map((day) => {
                                        const daySlots = slotsByDay[day.id];
                                        if (daySlots.length === 0) return null;

                                        return (
                                            <div key={day.id} className="day-schedule">
                                                <div className="day-header">
                                                    <i className="bi bi-calendar-day me-2"></i>
                                                    {day.name}
                                                </div>
                                                <div className="day-slots">
                                                    {daySlots.map((slot) => (
                                                        <div key={slot.id} className="time-slot-card">
                                                            <div className="slot-time">
                                                                <i className="bi bi-clock me-2"></i>
                                                                {slot.startTime} - {slot.endTime}
                                                            </div>
                                                            <button
                                                                type="button"
                                                                className="btn btn-sm btn-outline-danger"
                                                                onClick={() => handleDeleteTimeSlot(slot.id)}
                                                            >
                                                                <i className="bi bi-trash"></i>
                                                            </button>
                                                        </div>
                                                    ))}
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            ) : (
                                <div className="empty-state">
                                    <i className="bi bi-clock-history" style={{ fontSize: "3rem", color: "#ccc" }}></i>
                                    <p className="text-muted mt-2">No time slots added yet</p>
                                    <p className="text-muted">Click "Add Time Slot" to create your schedule</p>
                                </div>
                            )}
                        </div>

                        {/* Timezone */}
                        <div className="form-section">
                            <h5 className="section-title">
                                <i className="bi bi-globe me-2"></i>
                                Timezone
                            </h5>
                            <p className="text-muted mb-3">Select your timezone for accurate scheduling</p>

                            <div className="row">
                                <div className="col-md-6">
                                    <select
                                        className="form-select"
                                        value={formData.timezone}
                                        onChange={handleTimezoneChange}
                                    >
                                        {timezones.map((tz) => (
                                            <option key={tz.value} value={tz.value}>
                                                {tz.label}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                            </div>
                        </div>

                        {/* Action Buttons */}
                        <div className="form-actions">
                            <button
                                type="submit"
                                className="btn btn-danger btn-lg"
                                disabled={loading || completionPercentage < 100}
                            >
                                {loading ? (
                                    <>
                                        <span className="spinner-border spinner-border-sm me-2"></span>
                                        Saving...
                                    </>
                                ) : (
                                    <>
                                        <i className="bi bi-check-circle me-2"></i>
                                        Save Availability
                                    </>
                                )}
                            </button>
                            {completionPercentage < 100 && (
                                <p className="text-muted mt-2 mb-0">
                                    <i className="bi bi-info-circle me-1"></i>
                                    Select days and add time slots to save
                                </p>
                            )}
                        </div>
                    </form>
                )}

                {/* Time Slot Modal */}
                {showTimeSlotModal && (
                    <div className="modal-overlay" onClick={() => setShowTimeSlotModal(false)}>
                        <div className="modal-content" onClick={(e) => e.stopPropagation()}>
                            <div className="modal-header">
                                <h5>
                                    <i className="bi bi-clock me-2"></i>
                                    Add Time Slot
                                </h5>
                                <button
                                    type="button"
                                    className="btn-close"
                                    onClick={() => setShowTimeSlotModal(false)}
                                ></button>
                            </div>
                            <div className="modal-body">
                                <div className="mb-3">
                                    <label htmlFor="slotDay" className="form-label">
                                        Day <span className="text-danger">*</span>
                                    </label>
                                    <select
                                        className="form-select"
                                        id="slotDay"
                                        name="day"
                                        value={newTimeSlot.day}
                                        onChange={handleTimeSlotInputChange}
                                    >
                                        <option value="">Select Day</option>
                                        {daysOfWeek.map((day) => (
                                            <option key={day.id} value={day.id}>
                                                {day.name}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                                <div className="row g-3">
                                    <div className="col-6">
                                        <label htmlFor="startTime" className="form-label">
                                            Start Time <span className="text-danger">*</span>
                                        </label>
                                        <input
                                            type="time"
                                            className="form-control"
                                            id="startTime"
                                            name="startTime"
                                            value={newTimeSlot.startTime}
                                            onChange={handleTimeSlotInputChange}
                                        />
                                    </div>
                                    <div className="col-6">
                                        <label htmlFor="endTime" className="form-label">
                                            End Time <span className="text-danger">*</span>
                                        </label>
                                        <input
                                            type="time"
                                            className="form-control"
                                            id="endTime"
                                            name="endTime"
                                            value={newTimeSlot.endTime}
                                            onChange={handleTimeSlotInputChange}
                                        />
                                    </div>
                                </div>
                            </div>
                            <div className="modal-footer">
                                <button
                                    type="button"
                                    className="btn btn-secondary"
                                    onClick={() => setShowTimeSlotModal(false)}
                                >
                                    Cancel
                                </button>
                                <button type="button" className="btn btn-danger" onClick={handleAddTimeSlot}>
                                    <i className="bi bi-plus-circle me-2"></i>
                                    Add Time Slot
                                </button>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </DashboardLayout>
    );
}
